// src/contexts/Filter.context.jsx

'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';


// Create context with default values
const FiltersContext = createContext({

  // Raw methods
  filterState: {},
  updateFilterRaw: () => {},

  // Set methods for checkbox groups
  getFilterAsSet: () => new Set(),
  updateFilterSet: () => {},
  // addToFilterSet: () => {},
  // removeFromFilterSet: () => {},
  toggleInFilterSet: () => {},
  isInFilterSet: () => false,

  // Range methods
  getFilterRange: () => ({ min: '', max: '' }),
  updateFilterRange: () => {},

  // Radio/single value methods
  getFilterValue: () => '',
  updateFilterValue: () => {},

  // Utility methods
  hasFilter: () => false,
  // clearAllFilters: () => {},
  // clearFilter: () => {},

  // Access to original search params
  searchParams: new URLSearchParams()
});


export function FilterProvider({ children }) {

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Store all filter states in a single object
  const [filterState, setFilterState] = useState({});

  // Use a ref to track pending URL updates
  const pendingUpdate = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Initialize filter state from URL
  useEffect(() => {
    const initialState = {};

    // Parse all search params into our state
    for (const [key, value] of searchParams.entries()) {
      initialState[key] = value;
    }

    setFilterState(initialState);
  }, [searchParams]);

  // Debounced URL update function
  const updateURL = useCallback(() => {
    if (pendingUpdate.current) {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        const params = new URLSearchParams();

        // Convert filter state to search params
        Object.entries(pendingUpdate.current).forEach(([key, value]) => {
          if (value === '' || value === null || value === undefined) {
            // Skip empty values
          } else {
            params.set(key, value);
          }
        });

        const queryString = params.toString();
        const newUrl = pathname + (queryString ? `?${queryString}` : '');

        router.push(newUrl, { scroll: false });
        pendingUpdate.current = null;
      }, 10); // 10ms debounce
    }
  }, [pathname, router]);

  // Update a single filter with raw value
  const updateFilterRaw = useCallback((filterName, value) => {
    setFilterState(prev => {
      // If value is empty, remove the filter
      const newState = { ...prev };

      if (value === '' || value === null || value === undefined) {
        delete newState[filterName];
      } else {
        newState[filterName] = value;
      }

      pendingUpdate.current = newState;
      updateURL();
      return newState;
    });
  }, [updateURL]);

  // --- Specialized methods for different filter types ---

  // For checkbox groups (return and manage a Set)
  const getFilterAsSet = useCallback((filterName) => {
    const value = filterState[filterName];
    return value ? new Set(value.split(',')) : new Set();
  }, [filterState]);

  // Update a filter based on a Set
  const updateFilterSet = useCallback((filterName, valueSet) => {
    if (!(valueSet instanceof Set)) {
      console.error('updateFilterSet requires a Set');
      return;
    }

    const arrayValue = Array.from(valueSet);
    updateFilterRaw(filterName, arrayValue.length > 0 ? arrayValue.join(',') : '');
  }, [updateFilterRaw]);

  // Add a single value to a Set filter
  // const addToFilterSet = useCallback((filterName, value) => {
  //   const currentSet = getFilterAsSet(filterName);
  //   currentSet.add(value);
  //   updateFilterSet(filterName, currentSet);
  // }, [getFilterAsSet, updateFilterSet]);

  // Remove a single value from a Set filter
  // const removeFromFilterSet = useCallback((filterName, value) => {
  //   const currentSet = getFilterAsSet(filterName);
  //   currentSet.delete(value);
  //   updateFilterSet(filterName, currentSet);
  // }, [getFilterAsSet, updateFilterSet]);

  // Toggle a value in a Set filter (add if not present, remove if present)
  const toggleInFilterSet = useCallback((filterName, value) => {
    const currentSet = getFilterAsSet(filterName);
    if (currentSet.has(value)) {
      currentSet.delete(value);
    } else {
      currentSet.add(value);
    }
    updateFilterSet(filterName, currentSet);
  }, [getFilterAsSet, updateFilterSet]);

  // For range filters (min:100,max:500)
  const getFilterRange = useCallback((filterName) => {
    const value = filterState[filterName];
    if (!value) return { min: '', max: '' };

    try {
      const pairs = value.split(',');
      const result = {};

      pairs.forEach(pair => {
        const [key, val] = pair.split(':');
        if (key === 'min' || key === 'max') {
          result[key] = val;
        } else {
          result[key] = val; // For other properties like currency, units
        }
      });

      return {
        min: result.min || '',
        max: result.max || '',
        ...result
      };
    } catch (e) {
      console.error("Error parsing range filter:", e);
      return { min: '', max: '' };
    }
  }, [filterState]);

  // Update a range filter
  const updateFilterRange = useCallback((filterName, { min, max, ...rest }) => {
    const parts = [];

    if (min !== '' && min !== null && min !== undefined) {
      parts.push(`min:${min}`);
    }

    if (max !== '' && max !== null && max !== undefined) {
      parts.push(`max:${max}`);
    }

    // Add any additional properties
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        parts.push(`${key}:${value}`);
      }
    });

    updateFilterRaw(filterName, parts.length > 0 ? parts.join(',') : '');
  }, [updateFilterRaw]);

  // For radio filters (single value selection)
  const getFilterValue = useCallback((filterName) => {
    return filterState[filterName] || '';
  }, [filterState]);

  // Check if a filter exists and has a non-empty value
  const hasFilter = useCallback((filterName) => {
    return filterName in filterState && filterState[filterName] !== '';
  }, [filterState]);

  // Helper to check if a specific value is in a Set filter
  const isInFilterSet = useCallback((filterName, value) => {
    return getFilterAsSet(filterName).has(value);
  }, [getFilterAsSet]);

  // Helper to clear all filters
  // const clearAllFilters = useCallback(() => {
  //   setFilterState({});
  //   pendingUpdate.current = {};
  //   updateURL();
  // }, [updateURL]);

  // Helper to clear a specific filter
  // const clearFilter = useCallback((filterName) => {
  //   updateFilterRaw(filterName, '');
  // }, [updateFilterRaw]);

  const value = {
    // Raw methods
    filterState,
    updateFilterRaw,

    // Set methods for checkbox groups
    getFilterAsSet,
    updateFilterSet,
    // addToFilterSet,
    // removeFromFilterSet,
    toggleInFilterSet,
    isInFilterSet,

    // Range methods
    getFilterRange,
    updateFilterRange,

    // Radio/single value methods
    getFilterValue,
    updateFilterValue: updateFilterRaw,  // Alias for clarity

    // Utility methods
    hasFilter,
    // clearAllFilters,
    // clearFilter,

    // Access to original search params
    searchParams
  };

  return (
    <FiltersContext.Provider value={value}>
      {children}
    </FiltersContext.Provider>
  );
}


export const useFilters = () => useContext(FiltersContext);
